import React, { forwardRef, useEffect, useRef } from "react";
import { useCameraContext } from "./CameraContext.tsx";
import "./CameraCore.css";

export interface CameraCoreProps {
  className?: string;
  videoClassName?: string;
  children?: React.ReactNode;
}

const CameraCore = forwardRef<HTMLVideoElement, CameraCoreProps>(({
  className = "",
  videoClassName = "",
  children,
}, ref) => {
  const { videoRef, isInitializing, handleVideoElementChange } = useCameraContext();
  const localVideoRef = useRef<HTMLVideoElement>(null);

  // Effect to handle video element mounting and notify the scanner
  useEffect(() => {
    const videoElement = (ref as React.RefObject<HTMLVideoElement>)?.current || localVideoRef.current;
    
    if (videoElement) {
      // Update the context's videoRef to point to this element
      if (videoRef.current !== videoElement) {
        // Set the ref
        (videoRef as React.MutableRefObject<HTMLVideoElement | null>).current = videoElement;
        
        // Notify the scanner that the video element has changed
        handleVideoElementChange(videoElement);
      }
    }
  }, [ref, videoRef, handleVideoElementChange]);

  return (
    <div className={`camera-core ${className}`}>
      <div className="camera-core__container">
        <video
          ref={ref || localVideoRef}
          autoPlay
          playsInline
          muted
          className={`camera-core__video ${videoClassName}`}
        />
        {isInitializing && (
          <div className="camera-core__loading">
            <div className="camera-core__loading-spinner"></div>
            <div className="camera-core__loading-text">Initializing camera...</div>
          </div>
        )}
      </div>

      {children && (
        <div className="camera-core__overlay">
          {children}
        </div>
      )}
    </div>
  );
});

CameraCore.displayName = "CameraCore";

export default CameraCore; 